import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Quotes, Lightbulb } from '@phosphor-icons/react';
import { Button } from './ui/button';
import api from '../lib/api';

const DailyLessonModal = ({ lesson, onClose, autoMarkSeen = true }) => {
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    if (!autoMarkSeen || !lesson) return;
    let cancelled = false;
    (async () => {
      setMarking(true);
      try { await api.post('/learning/daily-lesson/seen'); } catch (e) { /* ignore */ }
      if (!cancelled) setMarking(false);
    })();
    return () => { cancelled = true; };
  }, [lesson?.id, autoMarkSeen]);

  if (!lesson) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[55] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="daily-lesson-modal"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg glass-card overflow-hidden relative"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-300 z-10"
            data-testid="lesson-close"
            aria-label="Close lesson"
          >
            <X size={18} />
          </button>

          <div className="bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-500 p-7 pb-12 relative">
            <p className="text-black/70 text-xs font-bold tracking-widest uppercase mb-1.5">📚 Today's Lesson</p>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-black/20 backdrop-blur mb-2">
              <BookOpen size={28} weight="fill" className="text-white" />
            </div>
            <h2 className="text-2xl font-unbounded font-bold text-white">{lesson.title}</h2>
            <p className="text-black/80 text-xs font-bold mt-1 uppercase tracking-wide">{lesson.concept}</p>
          </div>

          <div className="-mt-7 mx-5 mb-5 glass rounded-2xl p-5">
            <div className="flex gap-3 mb-4 pb-4 border-b border-white/10">
              <Quotes size={28} weight="fill" className="text-gold flex-shrink-0" />
              <div>
                <p className="text-zinc-100 italic leading-relaxed text-sm" data-testid="lesson-quote">
                  "{lesson.quote}"
                </p>
                <p className="text-xs text-zinc-500 mt-1.5">— {lesson.author}</p>
              </div>
            </div>

            <p className="text-zinc-200 text-sm leading-relaxed mb-4" data-testid="lesson-body">
              {lesson.body}
            </p>

            <div className="flex items-start gap-2 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
              <Lightbulb size={18} weight="fill" className="text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-yellow-100 text-xs font-medium" data-testid="lesson-takeaway">
                <span className="font-bold uppercase tracking-wider text-yellow-400">Key Takeaway: </span>
                {lesson.key_takeaway}
              </p>
            </div>
          </div>

          <div className="px-5 pb-5">
            <Button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full"
              data-testid="lesson-got-it"
            >
              Got it! 💡
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DailyLessonModal;
